import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box, Paper, Typography, IconButton, Tooltip} from '@mui/material'
import {ContentCopy as CopyIcon, Check as CheckIcon} from '@mui/icons-material'
import {Link} from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ToolSuggestionCard from './ToolSuggestionCard'
import SiteBuilderPlanCard from './SiteBuilderPlanCard'
import McpAppContainer, {type McpAppContainerHandle} from './McpAppContainer'
import ProgressStepCard from './ProgressStepCard'
import {RosAvatar, EchoAvatar, AnimatedEcho, RosHandoffCard, getMascot} from './RosMascots'
import type {ChatMessage, McpAppInputData} from './types'

interface MessageListProps {
  messages: ChatMessage[]
  isStreaming: boolean
  reachyAvatarUrl?: string
  /** Current active agent intent — used as default mascot for new/streaming messages */
  activeAgent?: string
  /** Dev mode — shows agent mascots, handoff cards, intent badges. Default: false (clean UX). */
  devMode?: boolean
  onSend?: (text: string) => void
  progressMessage?: string | null
  apiBaseUrl?: string
  /** Called when the MCP App sends a message to inject into chat and trigger an agent turn */
  onMcpMessage?: (text: string) => void
  /** Session ID — passed to MCP App for tool calls */
  sessionId?: string
  /** Called when the user approves a site builder plan — sets planApproved in DO then sends message */
  onApprovePlan?: (message: string) => void
  /** When true, MCP Apps render in a side panel — don't render inline */
  mcpPanelMode?: boolean
  /** Pending command to send to an active MCP App — consumed once and cleared */
  pendingMcpAppInput?: McpAppInputData | null
  onMcpAppInputConsumed?: () => void
}

const ExtractionProgress: React.FC<{message: string}> = ({message}) => (
  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 0.5}}>
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: 2,
        borderColor: 'primary.main',
        borderTopColor: 'transparent',
        animation: 'spin 0.8s linear infinite',
        flexShrink: 0,
        '@keyframes spin': {
          '0%': {transform: 'rotate(0deg)'},
          '100%': {transform: 'rotate(360deg)'}
        }
      }}
    />
    <Typography variant="body2" sx={{color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem'}}>
      {message}
    </Typography>
  </Box>
)


// Renders markdown with clickable internal links via React Router
const MarkdownContent: React.FC<{content: string; isStreaming?: boolean}> = ({content, isStreaming}) => (
  <Box
    sx={{
      '& p': {m: 0, lineHeight: 1.65, '& + p': {mt: 1}},
      '& ul, & ol': {pl: 2.5, m: 0, '& + *': {mt: 0.5}},
      '& li': {lineHeight: 1.65, mb: 0.25},
      '& strong': {fontWeight: 700},
      '& a': {color: 'primary.main', textDecoration: 'none', fontWeight: 500,
        '&:hover': {textDecoration: 'underline'}},
      '& code': {
        fontFamily: 'monospace',
        fontSize: '0.8em',
        bgcolor: 'action.hover',
        px: 0.5,
        py: 0.25,
        borderRadius: 0.5
      },
      fontSize: '0.875rem'
    }}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Internal links use React Router, external open in new tab
        a: ({href, children}) => {
          if (href?.startsWith('/')) {
            return <Link to={href}>{children}</Link>
          }
          return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
        },
        // Strip wrapping <p> margin for single-line responses
        p: ({children}) => <p style={{margin: 0, lineHeight: 1.65}}>{children}</p>,
        // HTML/CSS code blocks go to the artifact pane — hide them from chat entirely
        code: ({className, children, ...props}: any) => {
          const isBlock = className?.startsWith('language-')
          if (isBlock) {
            const language = className.replace('language-', '')
            if (language === 'html' || language === 'css') return null
          }
          return <code className={className} {...props}>{children}</code>
        },
        pre: ({children}: any) => <>{children}</>
      }}
    >
      {content}
    </ReactMarkdown>
  </Box>
)

// Matches a line that opens with a Rival Chattifyer tag.
// The `m` flag makes `^` match the start of any line, not just the string start.
const RIVAL_TAG_RE = /^\[(TEXT CARD|SINGLE CHOICE|MULTI.?CHOICE|OPEN END TEXT|NUMERIC[^\]]*|OPEN TEXT|BUTTON|TERMINATE[^\]]*|IF[^\]]*)\]/im

/**
 * Strip ```html and ```css code fences from message content.
 * These fences are generated by the website builder agent and displayed
 * in the ArtifactPane — showing them inline in the chat bubble is redundant.
 */
function stripCodeFences(content: string): string {
  return content.replace(/```(?:html|css)\s*\n[\s\S]*?```/g, '').replace(/\n{3,}/g, '\n\n').trim()
}

function splitInlineBullets(text: string): string[] {
  return text.split(/\s*•\s*/).map(s => s.trim()).filter(Boolean)
}

function normalizeRivalContent(raw: string): string {
  const lines = raw.split('\n')
  const out: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    const tagMatch = trimmed.match(RIVAL_TAG_RE)

    if (tagMatch) {
      if (out.length > 0 && out[out.length - 1] !== '') out.push('')
      out.push(tagMatch[0])
      const rest = trimmed.slice(tagMatch[0].length).trim()
      if (rest) splitInlineBullets(rest).forEach(p => out.push(p))
    } else if (trimmed) {
      const cleaned = trimmed.replace(/^[*\-]\s+/, '')
      splitInlineBullets(cleaned).forEach(p => out.push(p))
    } else {
      if (out.length > 0 && out[out.length - 1] !== '') out.push('')
    }
  }

  return out.join('\n').trim()
}

const PreformattedContent: React.FC<{content: string; isStreaming?: boolean}> = ({content, isStreaming}) => (
  <Box
    component="pre"
    sx={{
      m: 0, fontFamily: 'inherit', fontSize: '0.875rem', lineHeight: 1.65,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}
  >
    {normalizeRivalContent(content)}
  </Box>
)

// ── Quick suggestions ────────────────────────────────────────────────────────

const QUICK_SUGGESTIONS = [
  {text: 'Help me author a new survey',              color: '#9bb591'},
  {text: 'I need to build a deliverable from my data', color: '#8ea8c4'},
  {text: 'Help me plan my crosstabs',                color: '#b09bc4'},
  {text: 'Help me find bad respondents in my data',   color: '#d4ac7a'},
  {text: 'What can you help me with?',                color: '#7fb5b5'},
]

const QUICK_CATEGORIES: Array<{label: string; color: string; items: string[]}> = [
  {label: 'Survey Authoring', color: '#9bb591', items: [
    'Help me author a new survey',
    'I have a questionnaire document to convert',
    'Review my survey questions for quality',
    'Add AI probing to my survey',
  ]},
  {label: 'Deliverables', color: '#8ea8c4', items: [
    'Build a topline report from my data',
    'Create an interactive dashboard',
    'Generate a client-ready presentation',
  ]},
  {label: 'Crosstabs & Analysis', color: '#b09bc4', items: [
    'Help me plan my crosstabs',
    'Set up banner and stub variables',
    'Run significance testing on my tables',
  ]},
  {label: 'Data Quality', color: '#d4ac7a', items: [
    'Help me find bad respondents in my data',
    'Check for straight-liners and speeders',
    'Clean up open-ended responses',
  ]},
  {label: 'General', color: '#7fb5b5', items: [
    'What can you help me with?',
    'How does the survey authoring pipeline work?',
    'Explain the difference between monadic and sequential concept tests',
  ]},
]

const QuickMessageMenu: React.FC<{onSend?: (text: string) => void}> = ({onSend}) => {
  const [open, setOpen] = React.useState(false)

  return (
    <Box sx={{position: 'relative', display: 'inline-block'}}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          fontSize: '0.75rem', color: 'text.disabled', cursor: 'pointer',
          transition: 'color 0.12s', textAlign: 'center',
          '&:hover': {color: 'primary.main'},
        }}
      >
        More suggestions...
      </Box>

      {open && (<>
        {/* Backdrop */}
        <Box
          onClick={() => setOpen(false)}
          sx={{position: 'fixed', inset: 0, zIndex: 1200}}
        />
        {/* Floating popover */}
        <Box sx={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          mb: 1, zIndex: 1201,
          width: 520, maxHeight: 400, overflowY: 'auto',
          bgcolor: 'background.paper', borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid', borderColor: 'divider',
          p: 2, display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {QUICK_CATEGORIES.map(cat => (
            <Box key={cat.label}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.75}}>
                <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: cat.color}} />
                <Typography sx={{
                  fontSize: '0.68rem', fontWeight: 600, color: 'text.disabled',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {cat.label}
                </Typography>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
                {cat.items.map(item => (
                  <Box
                    key={item}
                    onClick={() => { onSend?.(item); setOpen(false) }}
                    sx={{
                      px: 1.5, py: 0.5, borderRadius: 1.5,
                      fontSize: '0.78rem', color: 'text.secondary',
                      cursor: 'pointer', transition: 'all 0.12s',
                      borderLeft: '2px solid transparent',
                      '&:hover': {
                        bgcolor: `${cat.color}12`, color: cat.color,
                        borderLeftColor: cat.color,
                      },
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </>)}
    </Box>
  )
}

const EmptyState: React.FC<{onSend?: (text: string) => void}> = ({onSend}) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 2.5,
      pt: 6,
      px: 4,
      pb: 4
    }}
  >
    <AnimatedEcho size={96} />

    <Box sx={{textAlign: 'center', maxWidth: 420}}>
      <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
        Hi, I'm Ros
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{lineHeight: 1.7}}>
        Your Rival Group OS in Studio. Tell me what you're working on - I'll help you build surveys, analyze data, create topline deliverables, or plan and build your crosstabs.
      </Typography>
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        justifyContent: 'center',
        maxWidth: 480
      }}
    >
      {QUICK_SUGGESTIONS.map(s => (
        <Box
          key={s.text}
          onClick={() => onSend?.(s.text)}
          sx={{
            px: 1.5, py: 0.75, borderRadius: 3,
            border: 1, borderColor: 'divider', bgcolor: 'background.paper',
            cursor: 'pointer', fontSize: '0.8rem', color: 'text.secondary',
            transition: 'all 0.12s',
            '&:hover': {borderColor: 'primary.main', color: 'primary.main', bgcolor: 'primary.50'}
          }}
        >
          {s.text}
        </Box>
      ))}
    </Box>

    {/* More suggestions */}
    <QuickMessageMenu onSend={onSend} />
  </Box>
)

function mdToPlainText(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/!\[.*?\]\(.+?\)/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

const CopyButton: React.FC<{text: string; isUser: boolean}> = ({text, isUser}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      // Assistant messages: strip markdown to plain text before copying
      const plain = isUser ? text : mdToPlainText(text)
      await navigator.clipboard.writeText(plain)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable — fail silently
    }
  }

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          opacity: 0,
          '.msg-row:hover &': {opacity: 1},
          transition: 'opacity 0.15s',
          color: isUser ? 'primary.contrastText' : 'text.secondary',
          p: 0.5,
          '&:hover': {bgcolor: isUser ? 'rgba(255,255,255,0.15)' : 'action.hover'}
        }}
      >
        {copied ? <CheckIcon sx={{fontSize: 14}} /> : <CopyIcon sx={{fontSize: 14}} />}
      </IconButton>
    </Tooltip>
  )
}

/**
 * Quiet "still working" line shown during a streaming turn's silent stretches —
 * after the model has streamed some text but gone quiet doing tool/reasoning work.
 * Without it, a paused stream reads as finished (the mascot animation is too subtle
 * to tell "thinking" from "idle"). One calm breathing line, never a list.
 */
const WorkingIndicator: React.FC = () => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 0.75, mt: 1,
      color: 'text.secondary', fontSize: '0.8rem',
      animation: 'rosWorkingBreathe 1.8s ease-in-out infinite',
      '@keyframes rosWorkingBreathe': {'0%,100%': {opacity: 0.55}, '50%': {opacity: 1}},
    }}
  >
    <Box
      component="span"
      sx={{
        width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0,
        animation: 'rosWorkingPulse 1.8s ease-in-out infinite',
        '@keyframes rosWorkingPulse': {'0%,100%': {transform: 'scale(0.8)'}, '50%': {transform: 'scale(1.3)'}},
      }}
    />
    <Typography variant="body2" component="span" sx={{fontSize: 'inherit'}}>Working···</Typography>
  </Box>
)

const MessageList: React.FC<MessageListProps> = ({messages, isStreaming, reachyAvatarUrl, activeAgent, devMode = false, onSend, onApprovePlan, progressMessage, apiBaseUrl, onMcpMessage, sessionId, mcpPanelMode, pendingMcpAppInput, onMcpAppInputConsumed}) => {
  const bottomRef = useRef<HTMLDivElement>(null)
  const mcpAppRefs = useRef<Map<string, McpAppContainerHandle>>(new Map())

  // Forward pending MCP App input commands to the right iframe
  useEffect(() => {
    if (!pendingMcpAppInput) return
    const handle = mcpAppRefs.current.get(pendingMcpAppInput.serverId)
    if (handle) {
      handle.sendToolInput(pendingMcpAppInput.arguments)
      onMcpAppInputConsumed?.()
    }
  }, [pendingMcpAppInput, onMcpAppInputConsumed])

  // "Stream paused" detection: the WorkingIndicator should only appear when the
  // streaming message has gone QUIET (tool/reasoning work), not while text is
  // actively arriving — otherwise it flickers under every chunk. We flip a flag
  // after a short gap with no content growth, and clear it the moment text resumes
  // or the turn ends.
  const streamingMsg = messages[messages.length - 1]
  const streamingLen = streamingMsg?.isStreaming ? streamingMsg.content.length : 0
  const [streamPaused, setStreamPaused] = useState(false)
  useEffect(() => {
    if (!isStreaming) { setStreamPaused(false); return }
    setStreamPaused(false) // content just changed (or streaming began) — not paused
    const timer = setTimeout(() => setStreamPaused(true), 400)
    return () => clearTimeout(timer)
  }, [isStreaming, streamingLen])

  // Auto-scroll only if user is near the bottom — respect manual scroll-up during streaming
  const containerRef = useRef<HTMLDivElement>(null)
  const userScrolledUpRef = useRef(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
      userScrolledUpRef.current = distanceFromBottom > 150
    }
    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!userScrolledUpRef.current) {
      bottomRef.current?.scrollIntoView({behavior: 'smooth'})
    }
  }, [messages, isStreaming])

  // Reset scroll lock when streaming ends — so next message auto-scrolls again
  useEffect(() => {
    if (!isStreaming) userScrolledUpRef.current = false
  }, [isStreaming])

  if (messages.length === 0) {
    return <EmptyState onSend={onSend} />
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}
    >
      {messages.map((msg) => (
        <React.Fragment key={msg.id}>
        {/* Agent handoff card — dev mode only */}
        {devMode && msg.role === 'assistant' && msg.handoff && (
          <RosHandoffCard from={msg.handoff.from} to={msg.handoff.to} />
        )}
        {/* Skip message bubble for handoff-only messages (no content) */}
        {(!msg.handoff || msg.content?.trim()) && <Box
          className="msg-row"
          sx={{
            display: 'flex',
            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
            gap: 1,
            maxWidth: 820,
            mx: 'auto',
            width: '100%',
          }}
        >
          {/* Avatar: dev mode shows per-agent mascots, clean mode shows Echo only during streaming.
              While the stream is PAUSED (silent work), a gentle breathe makes "still thinking"
              read more clearly — in sync with the WorkingIndicator, without editing shared keyframes. */}
          {msg.role === 'assistant' && (() => {
            const avatar = (() => {
              if (devMode) {
                const intent = msg.intent?.intent || (msg.isStreaming ? activeAgent : undefined)
                if (msg.isStreaming) {
                  const {animated: Animated} = getMascot(intent)
                  return <Animated size={48} />
                }
                return <RosAvatar intent={intent} size={32} />
              }
              // Clean mode: animated Echo at the top of streaming message, nothing for completed
              if (msg.isStreaming) return <AnimatedEcho size={36} />
              return null
            })()
            if (!avatar) return null
            const breathing = msg.isStreaming && streamPaused
            return (
              <Box
                sx={breathing ? {
                  animation: 'rosAvatarBreathe 1.8s ease-in-out infinite',
                  '@keyframes rosAvatarBreathe': {'0%,100%': {transform: 'scale(1)', opacity: 0.85}, '50%': {transform: 'scale(1.08)', opacity: 1}},
                } : undefined}
              >
                {avatar}
              </Box>
            )
          })()}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: msg.role === 'user' ? '75%' : '100%',
              flex: msg.role === 'assistant' ? 1 : undefined,
            }}
          >
            {/* Assistant: text on background (no card). User: subtle warm chip. */}
            {msg.role === 'assistant' ? (
              <Box sx={{wordBreak: 'break-word', width: '100%'}}>
                {msg.isStreaming && !msg.content && !progressMessage ? (
                  <Typography variant="body2" sx={{color: 'text.disabled', py: 1}}>...</Typography>
                ) : msg.isStreaming && !msg.content && progressMessage ? (
                  <ExtractionProgress message={progressMessage} />
                ) : (
                  <>
                    {RIVAL_TAG_RE.test(msg.content)
                      ? <PreformattedContent content={msg.content} isStreaming={msg.isStreaming && !progressMessage} />
                      : <MarkdownContent content={msg.isStreaming ? msg.content : stripCodeFences(msg.content)} isStreaming={msg.isStreaming && !progressMessage} />
                    }
                    {msg.isStreaming && progressMessage && (
                      <ExtractionProgress message={progressMessage} />
                    )}
                    {/* Silent-stretch indicator: text streamed, then went quiet on tool/
                        reasoning work. Only once the stream has actually paused, and when
                        no richer progress UI is already showing. */}
                    {msg.isStreaming && msg.content && !progressMessage && streamPaused && (
                      <WorkingIndicator />
                    )}
                    {!msg.isStreaming && msg.content && (
                      <Box sx={{display: 'flex', gap: 0.5, mt: 0.5, opacity: 0, '.msg-row:hover &': {opacity: 1}, transition: 'opacity 0.15s'}}>
                        <CopyButton text={msg.content} isUser={false} />
                      </Box>
                    )}
                  </>
                )}

                {msg.role === 'assistant' && !msg.isStreaming && msg.siteBuilderPlan && (onApprovePlan || onSend) && (
                  <SiteBuilderPlanCard data={msg.siteBuilderPlan} onApprove={onApprovePlan || onSend!} />
                )}

                {devMode && msg.role === 'assistant' && !msg.isStreaming && msg.intent && (
                  <ToolSuggestionCard intent={msg.intent} />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  px: 2, py: 1.25,
                  borderRadius: '20px',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  color: 'text.primary',
                  wordBreak: 'break-word',
                }}
              >
                <Typography variant="body2" sx={{lineHeight: 1.65, whiteSpace: 'pre-wrap'}}>
                  {msg.content}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>}

        {/* Progress steps — pipeline/agent step indicators */}
        {msg.role === 'assistant' && msg.progressSteps && msg.progressSteps.length > 0 && (
          <Box sx={{width: '100%', maxWidth: 820, mx: 'auto'}}>
            <ProgressStepCard steps={msg.progressSteps} />
          </Box>
        )}

        {/* MCP App — rendered inline only when panel mode is off (otherwise it's in the side panel) */}
        {msg.role === 'assistant' && msg.mcpApp && apiBaseUrl && !mcpPanelMode && (
          <Box sx={{width: '100%', maxWidth: 820, mx: 'auto'}}>
            <McpAppContainer
              ref={(handle) => {
                if (handle) mcpAppRefs.current.set(msg.mcpApp!.serverId, handle)
              }}
              serverId={msg.mcpApp.serverId}
              resourceUri={msg.mcpApp.resourceUri}
              toolName={msg.mcpApp.toolName}
              toolInput={msg.mcpApp.toolInput}
              toolResult={msg.mcpApp.toolResult}
              apiBaseUrl={apiBaseUrl}
              sessionId={sessionId}
              onMessage={onMcpMessage}
            />
          </Box>
        )}
        </React.Fragment>
      ))}

      {/* Ros presence — static Echo waiting when idle, dev mode thinking indicator when streaming */}
      {devMode && isStreaming && messages[messages.length - 1]?.role === 'user' && (() => {
        const Animated = getMascot(activeAgent).animated
        return (
          <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: 820, mx: 'auto', width: '100%'}}>
            <Animated size={48} />
            <Typography variant="body2" sx={{color: 'text.disabled', py: 1}}>...</Typography>
          </Box>
        )
      })()}
      {/* Clean mode: static Echo sits at the bottom when idle — like Claude's flower */}
      {!devMode && !isStreaming && messages.length > 0 && (
        <Box sx={{maxWidth: 820, mx: 'auto', width: '100%', mt: 0.5}}>
          <EchoAvatar size={28} />
        </Box>
      )}

      <div ref={bottomRef} />
    </Box>
  )
}

export default MessageList
